import * as React from "react";
import { Link } from "~/components/ui/link";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { RiArrowRightSLine, RiArrowDownSLine, RiArrowLeftSLine } from "@remixicon/react";
import ExternalLinkIcon from "~/assets/icons/ExternalLinkIcon";
import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import { Picture } from "~/components/ui/picture";
import { cn } from "~/lib/utils";
import { useContainerSize } from "~/lib/use-container-size";
import type { BlogPost } from "~/lib/blog-tags";

export type IconNavItem = {
    decorationLeft?: React.ReactNode;
    name: string;
    description?: string;
    href: string;
    badge?: string;
    // Shows the external-link glyph next to the title and opens the link in a new tab.
    external?: boolean;
    // Runs instead of following href (e.g. opening a drawer); the subnav still closes.
    onClick?: () => void;
};

export type SubnavId = "products" | "personal-use" | "fleet" | "resources";

// Grace period between the pointer leaving the nav and the panel closing, so a
// fast diagonal that clips outside the nav and comes straight back doesn't close it.
const SUBNAV_CLOSE_DELAY = 150;

// Grace period before a hovered trigger actually switches the active subnav, so
// briefly crossing over one trigger on the way to another (or down into the open
// panel) doesn't swap the panel out from under the pointer.
const SUBNAV_OPEN_DELAY = 100;

// Invisible strip directly under the panel: landing here (an overshoot past the
// panel's bottom edge) doesn't close it right away, since the pointer might just be
// correcting course back up into the panel.
export const SUBNAV_GUTTER_HEIGHT = 64;

// Longer than SUBNAV_CLOSE_DELAY on purpose — overshooting into the gutter is a much
// more likely accident than clipping outside the header entirely, so it gets a more
// generous grace period before closing.
export const SUBNAV_GUTTER_CLOSE_DELAY = 300;

// Coordinates a single "active" subnav across all nav triggers. One id drives one
// persistent <SubnavPanelShell>, so moving from one nav link to another swaps the
// panel's contents in place — `active` never passes through null, and the panel is
// never unmounted and remounted.
export function useActiveSubnav() {
    const [active, setActive] = React.useState<SubnavId | null>(null);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const openTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearPendingClose = () => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const clearPendingOpen = () => {
        if (openTimeoutRef.current !== null) {
            clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
        }
    };

    const openNow = React.useCallback((id: SubnavId) => {
        clearPendingClose();
        clearPendingOpen();
        setActive(id);
    }, []);

    const closeNow = React.useCallback(() => {
        clearPendingClose();
        clearPendingOpen();
        setActive(null);
    }, []);

    const closeWithDelay = React.useCallback((id: SubnavId, delay: number = SUBNAV_CLOSE_DELAY) => {
        clearPendingClose();
        timeoutRef.current = setTimeout(() => {
            setActive((current) => (current === id ? null : current));
        }, delay);
    }, []);

    const openWithDelay = React.useCallback((id: SubnavId) => {
        clearPendingClose();
        clearPendingOpen();
        openTimeoutRef.current = setTimeout(() => {
            setActive(id);
        }, SUBNAV_OPEN_DELAY);
    }, []);

    const cancelPendingOpen = React.useCallback(() => {
        clearPendingOpen();
    }, []);

    // Re-entering the nav (e.g. the pointer clips outside its rendered box for a
    // moment on a fast diagonal toward the panel, then lands back on it) should
    // call off a close that's already been scheduled.
    const cancelPendingClose = React.useCallback(() => {
        clearPendingClose();
    }, []);

    React.useEffect(() => () => {
        clearPendingClose();
        clearPendingOpen();
    }, []);

    return { active, openNow, closeNow, closeWithDelay, openWithDelay, cancelPendingOpen, cancelPendingClose };
}

export function InlineLinkRow({ href, label, onClick }: { href: string; label: string; onClick: () => void }) {
    return (
        <Link data-sid={`nav-inline-link:${href}`}
            to={href}
            onClick={onClick}
            className="bg-nav-surface-inset text-text-link fleet:text-text-body-tertiary flex w-full items-center justify-end gap-1 rounded-md px-2 py-2 text-sm font-bold underline"
        >
            {label}
            <RiArrowRightSLine className="h-5 w-5" />
        </Link>
    );
}

export function NavButton({
    open, anySubnavOpen, id, label, onClick,
}: {
    open: boolean; anySubnavOpen: boolean; id: SubnavId; label: string; onClick?: (e: React.MouseEvent) => void;
}) {
    return (
        <button
            type="button"
            data-sid={`nav-trigger-button:${id}`}
            onClick={onClick}
            aria-expanded={open}
            aria-haspopup="true"
            className="group my-auto flex h-9 items-center gap-2 rounded-md py-3 pr-1 pl-2"
        >
            <span
                className={cn(
                    "rounded-md whitespace-nowrap",
                    "font-sans text-base font-bold transition-colors",
                    // Padding on the pill, offset by an equal negative margin, so the
                    // highlight gets breathing room around the text without changing
                    // how much flex space this label (and so the button) consumes —
                    // the gap before the arrow (and the button's own right padding,
                    // above) is sized to match, so the pill's bleed lands exactly on
                    // the arrow's edge rather than over it.
                    "-mx-2 px-2 py-1",
                    open ? "bg-bouncie-100 text-text-heading fleet:bg-subnav-item-hover" : "bg-transparent",
                )}
            >
                {label}
            </span>
            <RiArrowDownSLine
                className={cn(
                    "h-4 w-4 shrink-0 transition-opacity duration-150",
                    anySubnavOpen && "opacity-0",
                )}
            />
        </button>
    )
}

// The one panel container, rendered once by the header for as long as *any*
// subnav is active. It deliberately does not remount when the active id changes:
// switching menus only swaps `children`, so the panel never collapses and
// re-expands on the way from one nav link to the next.
//
// Height can't be animated to "auto" here — motion resolves "auto" once, when the
// animation starts, and wouldn't re-run when the contents change size. Instead the
// body is measured and the shell animates to that pixel height, so a content swap
// simply retargets it and the box eases between the two sizes.
export function SubnavPanelShell({ children, onGutterEnter, onGutterLeave }: {
    children: React.ReactNode;
    // Wired to the gutter strip below — see the comment on it for why it's separate
    // from the panel's own hover handling.
    onGutterEnter?: () => void;
    onGutterLeave?: (e: React.MouseEvent) => void;
}) {
    const [bodyEl, setBodyEl] = React.useState<HTMLDivElement | null>(null);
    const { height } = useContainerSize(bodyEl);

    return (
        // Un-clipped on purpose: the gutter strip below needs to sit outside the
        // panel's own overflow-hidden (which exists only to mask its height
        // animation), so it stays hoverable past the panel's visible bottom edge.
        <div className="absolute inset-x-0 top-full z-[100]">
            <motion.div
                data-subnav-panel=""
                initial={{ height: 0 }}
                animate={{ height }}
                exit={{ height: 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="bg-nav-surface shadow-b-sm overflow-hidden rounded-b-xl"
            >
                {/* Only mount/unmount with the shell itself, so the slide-in plays when
                    the nav opens — not again on every switch between menus. The transform
                    doesn't affect the measured content box. */}
                <motion.div
                    ref={setBodyEl}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2, delay: 0.05 }}
                    className="grid w-full"
                >
                    {children}
                </motion.div>
            </motion.div>
            {/* Overshoot-recovery strip: landing here schedules a debounced close
                rather than closing on the spot, in case the pointer is just correcting
                back up into the panel. Moving past it, into real page content, closes
                instantly (handled by onGutterLeave). */}
            <div
                aria-hidden
                onMouseEnter={onGutterEnter}
                onMouseLeave={onGutterLeave}
                style={{ height: SUBNAV_GUTTER_HEIGHT }}
                className="w-full"
            />
        </div>
    );
}

// The contents of one menu's panel. Purely layout — the open/close and
// resize-between-menus animation all belong to the shell above.
export function SubnavPanelBody({ footer, children }: {
    footer?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <>
            <div className={cn("wrap flex flex-col items-center gap-8 pt-6", !footer && "pb-8")}>
                {children}
            </div>
            {footer && (
                // The shell's grid now stretches full width (see SubnavPanelShell),
                // so this row already spans the panel — the grey fill just needs to
                // look full-bleed past its own rounded corners, so it's a separate
                // layer that intentionally overflows and gets clipped by the panel's
                // own overflow-hidden.
                <div className="relative mt-8 min-h-17">
                    <div aria-hidden="true" className="bg-nav-surface-inset absolute inset-x-[-100vw] inset-y-0" />
                    <div className="wrap relative flex min-h-17 items-center justify-end gap-3">
                        {footer}
                    </div>
                </div>
            )}
        </>
    );
}

export function NavGroupHeader({ label }: { label: string }) {
    return (
        <p className="font-display fleet:text-neutral-200 px-2 pt-2 text-base font-bold tracking-[0.16px] text-neutral-300">{label}</p>
    );
}

export function NavColumn({ header, children, className }: { header: string; children: React.ReactNode; className?: string }) {
    return (
        <div className={cn("flex w-56 shrink-0 flex-col gap-4", className)}>
            <NavGroupHeader label={header} />
            {children}
        </div>
    );
}

export function ColumnDivider() {
    return <div className="border-border fleet:border-neutral-600 self-stretch border-l" />;
}

function ColumnButtonItem({ item, onNavigate }: { item: IconNavItem; onNavigate: () => void }) {
    return (
        <Link data-sid={`nav-column-item:${item.href}`}
            to={item.href}
            onClick={(e) => {
                if (item.onClick) {
                    e.preventDefault();
                    item.onClick();
                }
                onNavigate();
            }}
            {...(item.external ? { target: "_blank", rel: "noreferrer" } : {})}
            className="hover:bg-subnav-item-hover flex items-center gap-3 rounded-md px-2 py-2 transition-colors"
        >
            {item.decorationLeft}
            <div className="flex flex-col">
                <span className="text-text-body fleet:text-text-heading font-display inline-flex items-center gap-2 text-base font-bold tracking-[0.16px]">
                  {item.name}
                    {item.external && <ExternalLinkIcon className="shrink-0" />}
                    {item.badge && <Badge variant="default" className="ml-2">{item.badge}</Badge>}
                </span>
                { item.description && <span className="text-nav-item-subtext text-xs">{item.description}</span>}
            </div>
        </Link>
    );
}

export function ColumnButtons({
    onNavigate,
    buttonData,
}: {
    onNavigate: () => void;
    buttonData: IconNavItem[];
}) {
    return (
        <ul className="flex flex-col items-start gap-0">
            {buttonData.map((item) => {
                return (
                    <li key={item.name} className="w-full">
                        <ColumnButtonItem item={item} onNavigate={onNavigate} />
                    </li>
                );
            })}
        </ul>
    );
}

export function NavIcon({ icon: Icon, tone = "light" }: { icon: React.ElementType; tone?: "light" | "dark" }) {
    return (
        <div className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-md",
            tone === "dark" ? "bg-bouncie-900" : "bg-nav-surface-inset",
        )}>
            <Icon className={cn("h-6 w-6", tone === "dark" ? "text-white" : "text-icon-primary")} />
        </div>
    );
}

export function NavThumb({ src, alt, className, fit = "cover" }: { src: string; alt: string; className?: string; fit?: "cover" | "contain" }) {
    return (
        <div className={cn("bg-nav-surface-inset flex h-[108px] w-[126px] shrink-0 items-center justify-center overflow-hidden rounded-md p-2", className)}>
            {/* Fixed px, not h-full/w-full: this box's direct parent is a plain
                <picture> with no explicit height, so a percentage height can't
                resolve against it — the browser falls back to the image's
                natural aspect ratio and overflow-hidden silently crops it. */}
            <Picture src={src} alt={alt} className={cn("h-[92px] w-[110px]", fit === "cover" ? "object-cover" : "object-contain")} />
        </div>
    );
}

export function ProductCard({ onNavigate, href, src, alt, name, description, badge }: {
    onNavigate: () => void;
    href: string; src: string; alt: string; name: string; description: string; badge?: string;
}) {
    return (
        <Link data-sid={`nav-product-card:${href}`}
            to={href}
            onClick={onNavigate}
            className="hover:bg-subnav-item-hover group relative flex w-[230px] shrink-0 flex-col items-center gap-2 rounded-md p-2 text-center transition-colors"
        >
            {/* Figma positions this 4px/126px from a zero-padding wrapper nested
                inside the card's own 8px padding — since this Badge is a direct
                child of the padded card instead, both offsets add that 8px back. */}
            {badge && (
                <Badge variant="default" className="bg-figma-grey-mist text-figma-grey-onyx absolute top-[4px] left-[134px] opacity-0 transition-opacity group-hover:opacity-100">{badge}</Badge>
            )}
            <Picture src={src} alt={alt} className="h-[92px] w-[110px] object-contain" />
            <div className="flex flex-col items-center">
                <span className="text-text-body fleet:text-text-heading font-display text-base font-bold tracking-[0.16px]">{name}</span>
                <span className="text-nav-item-subtext text-sm">{description}</span>
            </div>
        </Link>
    );
}

// How long each promoted post stays on screen before the carousel auto-advances.
const NAV_CARD_ROTATE_INTERVAL_MS = 4000;

export function NavCard({ tone = "light", image, title, description, className, href, learnMoreLabel, posts, isLoading }: {
    tone?: "light" | "dark";
    image: string;
    title: string;
    description?: string;
    className?: string;
    href?: string;
    learnMoreLabel?: string;
    // When there's more than one promoted post, auto-rotate through them on a
    // fixed timer — prev/next/pip controls let a visitor override the timing.
    posts?: BlogPost[];
    // Set while the promoted posts for this card are still being fetched, so a
    // skeleton renders instead of the static placeholder image/title/description.
    isLoading?: boolean;
}) {
    const { t } = useTranslation("common");
    const [index, setIndex] = React.useState(0);
    const hasMultiple = (posts?.length ?? 0) > 1;
    const active = posts && posts.length > 0 ? posts[index % posts.length] : undefined;

    const resolvedImage = active?.imageUrl ?? image;
    const resolvedTitle = active?.title ?? title;
    const resolvedDescription = active?.excerpt ?? description;
    const resolvedHref = active?.url ?? href;

    // Once a visitor manually steps through the carousel, auto-rotation stops
    // for good (not just paused) — it won't restart until this NavCard remounts.
    const hasInteractedRef = React.useRef(false);
    const intervalIdRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

    React.useEffect(() => {
        if (!hasMultiple || hasInteractedRef.current) return;
        intervalIdRef.current = setInterval(() => {
            setIndex((i) => (i + 1) % posts!.length);
        }, NAV_CARD_ROTATE_INTERVAL_MS);
        return () => {
            if (intervalIdRef.current !== null) clearInterval(intervalIdRef.current);
        };
    }, [hasMultiple, posts]);

    function goTo(e: React.MouseEvent, next: number) {
        // Blocks the click from reaching the card's own link — lets the pips
        // and arrows be operated without accidentally following the card href.
        e.preventDefault();
        e.stopPropagation();
        hasInteractedRef.current = true;
        if (intervalIdRef.current !== null) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }
        setIndex(next);
    }

    const skeletonContent = (
        <div className="flex flex-col gap-4">
            <Skeleton className="h-[140px] w-full rounded-xl" />
            <div className="flex min-h-[12.75rem] flex-col gap-2">
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-3/5" />
                <Skeleton className="mt-2 h-3 w-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
            </div>
        </div>
    );

    const content = (
        <>
            {/* Scoped to just the image/text — hovering the carousel controls below
                (a sibling, not a descendant) must not trigger this "group-hover". */}
            <div className="group/nav flex flex-col">
                {/* `active` (a CMS blog post) supplies an external imageUrl; Picture only adds
                    <source>s for AVIF/WebP twins that exist, so it is safe for those too. */}
                <Picture src={resolvedImage} alt="" className="mb-6 h-[140px] w-full rounded-xl object-cover" />
                {/* Fixed line-clamps + min-height keep the card's height steady as the
                    carousel rotates through posts with differently-sized titles/excerpts —
                    only needed when there's actually a carousel to steady; a static card
                    (no `posts`) should just size to its own content within the outer
                    card's min-height instead. */}
                <div className={cn("flex flex-col gap-1", hasMultiple && "min-h-[12.75rem]")}>
                    <p className={cn(
                        "font-display line-clamp-4 text-base font-bold tracking-[0.16px]",
                        tone === "dark" ? "text-white" : "text-text-heading",
                        "fleet:text-text-heading",
                    )}>{resolvedTitle}</p>
                    {resolvedDescription && (
                        <p className={cn(
                            "line-clamp-4 text-sm",
                            tone === "dark" ? "text-card-fill-grey" : "text-text-body-alt",
                            "fleet:text-text-body",
                        )}>{resolvedDescription}</p>
                    )}
                    {/* Decorative only — the whole card is already the link, so this can't be a nested <a>. */}
                    {resolvedHref && learnMoreLabel && (
                        <span className={cn(
                            "inline-flex items-center gap-1 text-sm font-bold",
                            tone === "dark" ? "text-white" : "text-bouncie-500",
                            "fleet:text-text-body-tertiary",
                        )}>
                            <span className="group-hover/nav:underline">{learnMoreLabel}</span>
                            <RiArrowRightSLine className="size-4" />
                        </span>
                    )}
                </div>
            </div>
            {hasMultiple && (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- click boundary only, not a new interactive control; a stray click in the gaps between pips/arrows shouldn't fall through to the card's link, and the buttons inside remain real, keyboard-operable controls
                <div
                    className="mt-auto flex cursor-default items-center justify-between"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                >
                    <button
                        type="button"
                        onClick={(e) => goTo(e, (index - 1 + posts!.length) % posts!.length)}
                        aria-label={t("blog.previous_post")}
                        className={cn(
                            "flex size-6 cursor-pointer items-center justify-center rounded-full transition-colors",
                            tone === "dark"
                                ? "bg-white/10 text-white hover:bg-white/25"
                                : "border border-border bg-background text-text-heading shadow-sm hover:border-bouncie-500 hover:bg-bouncie-500 hover:text-white",
                        )}
                    >
                        <RiArrowLeftSLine className="size-4" />
                    </button>
                    <div className="flex items-center gap-1">
                        {posts!.map((post, i) => (
                            <button
                                key={post.id}
                                type="button"
                                onClick={(e) => goTo(e, i)}
                                aria-label={t("blog.go_to_post", { number: i + 1 })}
                                aria-current={i === index}
                                className={cn(
                                    "h-1.5 cursor-pointer rounded-full transition-all",
                                    i === index
                                        ? (tone === "dark" ? "w-4 bg-white" : "w-4 bg-bouncie-500")
                                        : (tone === "dark"
                                            ? "w-1.5 bg-white/40 hover:bg-white/60"
                                            : "w-1.5 bg-neutral-200 hover:bg-neutral-300"),
                                )}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={(e) => goTo(e, (index + 1) % posts!.length)}
                        aria-label={t("blog.next_post")}
                        className={cn(
                            "flex size-6 cursor-pointer items-center justify-center rounded-full transition-colors",
                            tone === "dark"
                                ? "bg-white/10 text-white hover:bg-white/25"
                                : "border border-border bg-background text-text-heading shadow-sm hover:border-bouncie-500 hover:bg-bouncie-500 hover:text-white",
                        )}
                    >
                        <RiArrowRightSLine className="size-4" />
                    </button>
                </div>
            )}
        </>
    );

    const cardClassName = cn(
        "flex w-[220px] flex-col gap-4 self-stretch rounded-xl p-6 min-h-[323px]",
        tone === "dark" ? "bg-[#060b14]" : "bg-card-fill-grey",
        "fleet:bg-nav-surface-inset",
        className,
    );

    if (isLoading) {
        return <div className={cardClassName}>{skeletonContent}</div>;
    }

    if (resolvedHref) {
        return (
            <a data-sid={`nav-card-link:${resolvedHref}`} href={resolvedHref} target="_blank" rel="noreferrer" className={cn(cardClassName, "cursor-pointer")}>
                {content}
            </a>
        );
    }

    return <div className={cardClassName}>{content}</div>;
}
